import axiosWithAuthInterceptor from "../util/axiosWithAuthInterceptor";
import {
  apiDefaultToForm,
  apiRuleToForm,
  formDefaultToApi,
  formRuleToApi,
  normalizeApiDefaultPopup,
  normalizeApiRule,
} from "./mapping";
import { resolvePopupFromRules, ResolveUtmParams } from "./resolve";
import {
  ApiCampaignPopupDefault,
  ApiCampaignPopupRule,
  BUILTIN_DEFAULT_POPUP,
  CampaignPopupRule,
  CampaignPopupsConfig,
  CONFIG_KEY,
} from "./types";

export const configPublicUrl = () =>
  `${process.env.REACT_APP_LEMONADE_SERVER}/api/limoome-integrations/config/${CONFIG_KEY}`;

/** Base URL for lemonade-front preview links (hash router). */
export function getLemonadeFrontBaseUrl(): string {
  const fromEnv =
    process.env.REACT_APP_LEMONADE_FRONT ||
    process.env.REACT_APP_AUTH_PUBLIC_PATH;
  if (fromEnv && String(fromEnv).trim()) {
    return String(fromEnv).replace(/\/$/, "");
  }

  const limoome = process.env.REACT_APP_LIMOOME_SERVER || "";
  if (limoome.includes("my.limoome.com")) {
    return "https://my.limoome.com/good-look-new/";
  }
  if (limoome.includes("my.limoome.app")) {
    return "https://my.limoome.app/good-look-new/";
  }

  return "";
}

export function extractApiRulesFromResponse(data: unknown): ApiCampaignPopupRule[] {
  return parseCampaignPopupsConfigFromResponse(data).rules;
}

export function parseCampaignPopupsConfigFromResponse(
  data: unknown
): {
  enabled: boolean;
  rules: ApiCampaignPopupRule[];
  defaultPopup: ApiCampaignPopupDefault;
} {
  if (data == null) {
    return {
      enabled: true,
      rules: [],
      defaultPopup: formDefaultToApi({ ...BUILTIN_DEFAULT_POPUP }),
    };
  }

  let payload: unknown = data;
  if (typeof data === "object" && !Array.isArray(data)) {
    const o = data as Record<string, unknown>;
    if (Array.isArray(o.rules)) {
      const defaultRaw =
        o.defaultPopup ?? o.default ?? BUILTIN_DEFAULT_POPUP;
      return {
        enabled: o.enabled !== false,
        rules: mapRulesArray(o.rules),
        defaultPopup:
          typeof defaultRaw === "object" && defaultRaw !== null
            ? normalizeApiDefaultPopup(defaultRaw as Record<string, unknown>)
            : formDefaultToApi(BUILTIN_DEFAULT_POPUP),
      };
    }
    if (typeof o.value === "string") {
      try {
        return parseCampaignPopupsConfigFromResponse(JSON.parse(o.value));
      } catch {
        return {
          enabled: true,
          rules: [],
          defaultPopup: formDefaultToApi({ ...BUILTIN_DEFAULT_POPUP }),
        };
      }
    }
    if (Array.isArray(o.value)) {
      return {
        enabled: true,
        rules: mapRulesArray(o.value),
        defaultPopup: formDefaultToApi({ ...BUILTIN_DEFAULT_POPUP }),
      };
    }
    if (o.data != null) return parseCampaignPopupsConfigFromResponse(o.data);
  }

  if (Array.isArray(payload)) {
    return {
      enabled: true,
      rules: mapRulesArray(payload),
      defaultPopup: formDefaultToApi({ ...BUILTIN_DEFAULT_POPUP }),
    };
  }

  return {
    enabled: true,
    rules: [],
    defaultPopup: formDefaultToApi({ ...BUILTIN_DEFAULT_POPUP }),
  };
}

function mapRulesArray(items: unknown[]): ApiCampaignPopupRule[] {
  return items.map((item) =>
    normalizeApiRule(
      typeof item === "object" && item !== null
        ? (item as Record<string, unknown>)
        : {}
    )
  );
}

export async function getCampaignPopupsConfig(): Promise<CampaignPopupsConfig> {
  const res = await axiosWithAuthInterceptor.get(configPublicUrl());
  if (res.data?.err) {
    throw new Error(
      typeof res.data.err.message === "string"
        ? res.data.err.message
        : "خطا در دریافت تنظیمات"
    );
  }
  const parsed = parseCampaignPopupsConfigFromResponse(res.data);
  return {
    enabled: parsed.enabled,
    rules: parsed.rules.map(apiRuleToForm),
    defaultPopup: apiDefaultToForm(parsed.defaultPopup),
  };
}

export async function putCampaignPopupsConfig(
  config: CampaignPopupsConfig
): Promise<CampaignPopupsConfig> {
  const body = {
    enabled: config.enabled,
    rules: config.rules.map(formRuleToApi),
    defaultPopup: formDefaultToApi(config.defaultPopup),
  };
  const res = await axiosWithAuthInterceptor.put(configPublicUrl(), body);
  if (res.data?.err) {
    throw new Error(
      typeof res.data.err.message === "string"
        ? res.data.err.message
        : "خطا در ذخیره تنظیمات"
    );
  }
  const parsed = parseCampaignPopupsConfigFromResponse(res.data);
  return {
    enabled: parsed.enabled,
    rules:
      parsed.rules.length > 0 || body.rules.length === 0
        ? parsed.rules.map(apiRuleToForm)
        : config.rules,
    defaultPopup: apiDefaultToForm(parsed.defaultPopup),
  };
}

/** Client-side resolve (same rules as public frontend). */
export async function resolveCampaignPopup(
  params: ResolveUtmParams
): Promise<ApiCampaignPopupRule | null> {
  const res = await axiosWithAuthInterceptor.get(configPublicUrl());
  if (res.data?.err) {
    throw new Error(
      typeof res.data.err.message === "string"
        ? res.data.err.message
        : "خطا در دریافت تنظیمات"
    );
  }
  const { rules } = parseCampaignPopupsConfigFromResponse(res.data);
  return resolvePopupFromRules(rules, params);
}

export function buildPreviewHashLink(rule: CampaignPopupRule): string {
  const q = new URLSearchParams();
  const campaign =
    rule.campaign.split(",")[0]?.trim() || rule.campaign.trim();
  q.set("utm_campaign", campaign);
  if (rule.utm_source?.trim()) q.set("utm_source", rule.utm_source.trim());
  if (rule.utm_medium?.trim()) q.set("utm_medium", rule.utm_medium.trim());
  const base = getLemonadeFrontBaseUrl();
  return `${base}#/newbetterme?${q.toString()}`;
}
